/* eslint-disable no-case-declarations */
import { Table } from 'antd';
import BaseStructure from 'components/BaseStructure';
import { useRoutesDisplayValues } from 'hooks/useRoutesDisplayValues';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import AuthSelectors from 'redux/auth/auth.selectors';
import ROUTES from 'routes/routeConstants';
import { DocDirection, IDocumentLog } from 'types/documentLog';

import ApiEndpoint from '../../services/api/apiEndpoint';

const SystemLog: React.FC = () => {
  const token = useSelector(AuthSelectors.getAccessToken);
  const currentLog = useRef<IDocumentLog[]>([]);
  const updateLength = useRef(0);
  const lastUpdate = useRef(new Date().getTime());
  const [logs, setLogs] = useState<IDocumentLog[]>([]);

  useEffect(() => {
    const sse = new EventSource(`${process.env.REACT_APP_BACKEND_URL}${ApiEndpoint.documentLog}?token=${token}`);
    const onMsgArrive = (data: IDocumentLog) => {
      currentLog.current = [data, ...currentLog.current];
      lastUpdate.current = new Date().getTime();
    };

    let intervalSetState: NodeJS.Timeout;

    sse.onopen = () => {
      intervalSetState = setInterval(() => {
        const current = new Date().getTime();
        if (current - lastUpdate.current > 200 && currentLog.current.length !== updateLength.current) {
          const newLogs = JSON.parse(JSON.stringify(currentLog.current)) as IDocumentLog[];
          setLogs(newLogs);
          updateLength.current = newLogs.length;
        }
      }, 1000);
    };

    sse.onmessage = (e) => {
      onMsgArrive(JSON.parse(e.data));
    };
    sse.onerror = () => {
      sse.close();
    };
    return () => {
      sse.close();
      clearInterval(intervalSetState);
    };
  }, []);

  const columns = [
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
    },
    {
      title: 'Transaction',
      dataIndex: 'transaction',
      key: 'transaction',
      render: ({ transactionRef, transactionId }: { transactionRef: string; transactionId: string }) =>
        transactionId && (
          <a
            target='blank'
            href={`http${
              process.env.NODE_ENV === 'production' || (process.env.NODE_ENV as string) === 'staging' ? 's' : ''
            }://${window.location.host}/transaction/${transactionId}`}>
            {transactionRef}
          </a>
        ),
    },
  ];

  const dataSource = logs.map((log) => {
    const logTime = moment(log.createdAt.toString());
    return {
      key: log.id,
      time: logTime.format('ddd, MMM D | HH:MM'),
      message: log.details.message
        ? log.details.message
        : `${log.details.email} ${log.details.direction === DocDirection.DOWNLOAD ? 'downloaded' : 'uploaded'} ${
            log.details.name
          }`,
      transaction: log.details,
    };
  });

  const routeDisplayValue = useRoutesDisplayValues()[ROUTES.SYSTEM_LOG];

  return (
    <BaseStructure header={routeDisplayValue.text} headerIconName={routeDisplayValue.iconClass}>
      <Table dataSource={dataSource} columns={columns} pagination={false} style={{ width: '100%' }} />
    </BaseStructure>
  );
};

export default SystemLog;
